import { Inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ACCOUNT_NUMBER, PHONE_NUMBER, SELECTED_SUBSCRIBER_NUMBER } from '@rci/self-serve-fido/app.constant';
import { SelfServeAppState } from '@rci/self-serve-fido/state/app.state.model';
import { StateService } from '@rci/state-service';
import { LOCAL_STORAGE } from '@rci/storage/local-storage.service';
import { UserAccount } from '@rogers/self-serve-common';
import { AccountDetailFacadeService, AccountListFacadeService } from '@rogers/self-serve-common/account';
import { getLatestValue } from '@rogers/self-serve-utils';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import {
  resetUserInformation,
  updateSelectedAccountNumber,
  updateSelectedSubscriberNo,
  updateUserInformation,
} from './actions';
import { getSelectedAccountNumber, getUserType, userInformationState } from './selectors';
import { UserInformation } from './user-information';

@Injectable({ providedIn: 'root' })
export class UserInformationStateFacade {
  selectedAccountNumber$ = this.store.pipe(
    select(getSelectedAccountNumber),
    filter(accountNumber => !!accountNumber),
  );

  constructor(
    private readonly accountDetailFacadeService: AccountDetailFacadeService,
    private readonly accountListFacadeService: AccountListFacadeService,
    private readonly store: Store<SelfServeAppState>,
    private readonly stateService: StateService,
    @Inject(LOCAL_STORAGE) private readonly localStorage: Storage,
  ) {}

  get userInformation(): UserInformation {
    return getLatestValue<UserInformation>(this.getUserInformation());
  }

  get selectedAccountNumber(): string {
    return getLatestValue<string>(this.getSelectedAccountNumber());
  }

  get selectedPhoneNumber(): string {
    return getLatestValue<string>(this.getSelectedPhoneNumber());
  }

  get isOwnerOrGroupId(): boolean {
    return this.isOwner || this.isGroupId;
  }

  get isSubscriberOrGroupId(): boolean {
    return this.isSubscriber || this.isGroupId;
  }

  get userName(): string {
    return getLatestValue<string>(this.getUserName());
  }

  get isSubscriber(): boolean {
    return this.selectedAccount?.isSubscriber;
  }

  get isOwner(): boolean {
    return this.selectedAccount?.isOwner;
  }

  get isGroupId(): boolean {
    return this.selectedAccount?.isGroupId;
  }

  get firstName$(): Observable<string> {
    return this.getUserInformation().pipe(map(userInfo => userInfo.firstName || ''));
  }

  get selectedAccount(): UserAccount {
    return getLatestValue<UserAccount>(this.selectedAccount$);
  }

  get selectedAccount$() {
    return this.accountListFacadeService.getAccount$(this.selectedAccountNumber);
  }

  resetUserInformation() {
    this.store.dispatch(resetUserInformation());
  }

  updateUserInformation(userInformation: UserInformation) {
    this.store.dispatch(updateUserInformation({ userInfo: userInformation }));
  }

  updateSelectedAccountNumber(accountNumber: string) {
    this.localStorage.setItem(ACCOUNT_NUMBER, accountNumber);
    this.store.dispatch(updateSelectedAccountNumber({ accountNumber }));
    this.setAccountAlias$(accountNumber);
  }

  updateSelectedSubscriberNo(subscriberNo: string) {
    if (this.isWirelessAccount()) {
      this.localStorage.setItem(PHONE_NUMBER, subscriberNo);
      this.setSubscriptionAlias$(subscriberNo);
    }
    if (this.isInternetAccount()) {
      this.localStorage.setItem(SELECTED_SUBSCRIBER_NUMBER, subscriberNo);
    }
    this.store.dispatch(updateSelectedSubscriberNo({ subscriberNo }));
  }

  getSelectedAccountNumber(): Observable<string> {
    return this.store.pipe(
      select(getSelectedAccountNumber),
      map(accountNumber => {
        return accountNumber || '';
      }),
    );
  }

  getSelectedPhoneNumber(): Observable<string> {
    return this.getUserInformation().pipe(map(userInfo => userInfo.selectedSubscriberNo || ''));
  }

  getUserType(): Observable<string> {
    return this.store.pipe(
      select(getUserType),
      filter(userType => !!userType),
    );
  }

  getUserName(): Observable<string> {
    return this.stateService.loginState$.pipe(map(loginState => (loginState ? loginState.userName : '')));
  }

  private getUserInformation(): Observable<UserInformation> {
    return this.store.pipe(
      select(userInformationState),
      filter(userInfo => !!userInfo),
    );
  }

  private isWirelessAccount() {
    return this.selectedAccount.isWirelessOrConsolidatedAccount;
  }

  private isInternetAccount() {
    return this.selectedAccount.isCableOrInternetAccount;
  }

  private setAccountAlias$(accountNumber: string) {
    return this.accountListFacadeService
      .getAccount$(accountNumber)
      .pipe(
        tap(account => {
          if (account) {
            this.localStorage.setItem('accountAlias', account.alias);
          }
        }),
        take(1),
      )
      .subscribe();
  }

  private setSubscriptionAlias$(phoneNumber: string) {
    return this.selectedAccountNumber$
      .pipe(
        switchMap(selectedAccountNumber =>
          this.accountDetailFacadeService.getSubscriptionsForPageDisplay$(selectedAccountNumber),
        ),
        filter(subscriptions => subscriptions && subscriptions.length > 0),
        tap(subscriptions => {
          const subscriptionAlias = subscriptions.find(sub => sub.subNumber === phoneNumber)?.alias;
          if (subscriptionAlias) {
            this.localStorage.setItem('subscriptionAlias', subscriptionAlias);
          }
        }),
        take(1),
      )
      .subscribe();
  }
}
